/* Copyright (c) 2020-present CloudIO Inc */

import { atom } from '@cloudio-saas/statex';
import React, { createContext, useContext, useRef } from 'react';
import {
  BaseSelection,
  Editor,
  Element as SlateElement,
  Node as SlateNode,
  Transforms,
} from 'slate';
import { useSlate } from 'slate-react';
import { BlockType, LIST_TYPES, MarkType } from './SlateTypes';

export const floatingActionPositionAtom = atom<{
  type: BlockType;
  top: number;
  toolbarVisible: boolean;
  linkPopoverVisible: boolean;
  emptyLine: boolean;
}>({
  path: ['transient', 'slate', 'floatingActionPosition'],
  defaultValue: {
    top: -1,
    type: 'p',
    toolbarVisible: false,
    emptyLine: false,
    linkPopoverVisible: false,
  },
});

export const toggleBlock = (editor: Editor, blockType: BlockType) => {
  const isActive = isBlockActive(editor, blockType);
  const isList = LIST_TYPES.includes(blockType);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      LIST_TYPES.includes(
        !Editor.isEditor(n) && SlateElement.isElement(n) ? n.type : 'p',
      ),
    split: true,
  });
  if (isActive && blockType === 'quote') {
    Transforms.unwrapNodes(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === 'quote',
      split: true,
    });
  }

  if (blockType !== 'quote') {
    const newProperties: Partial<SlateElement> = {
      type: isActive ? 'p' : isList ? 'li' : blockType,
    };
    editor.skipListNormalizing = true;
    Transforms.setNodes(editor, newProperties);
    editor.skipListNormalizing = false;
  }

  if (!isActive && isList) {
    const block = { type: blockType, children: [] };
    Transforms.wrapNodes(editor, block);
  }

  if (!isActive && blockType === 'quote') {
    const block = { type: blockType, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const toggleMark = (editor: Editor, format: MarkType) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const isBlockActive = (editor: Editor, format: BlockType) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => {
      return (
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format
      );
    },
  });

  return !!match;
};

export const isMarkActive = (editor: Editor, format: MarkType) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const setMarkStyleForColor = (
  editor: Editor,
  type: string,
  valueOfColor: string,
) => {
  Editor.addMark(editor, type, valueOfColor);
};

export const SlateContext = createContext<{
  editor: Editor | null;
  lastSelection: BaseSelection | null;
  x?: number;
}>({
  editor: null,
  lastSelection: null,
  x: 1,
});

export const SlateContextUpdater: React.FunctionComponent = () => {
  const editor = useSlate();
  const ctx = useContext(SlateContext);
  ctx.editor = editor;
  if (editor.selection) {
    ctx.lastSelection = editor.selection;
  }
  ctx.x = 3;
  return null;
};

export const SlateProvider: React.FunctionComponent<{
  children: React.ReactNode;
}> = ({ children }) => {
  const ctx = useRef({ editor: null, lastSelection: null, x: 2 });
  return (
    <SlateContext.Provider value={ctx.current}>
      <SlateContextUpdater />
      {children}
    </SlateContext.Provider>
  );
};

export function useSlateContext() {
  return useContext(SlateContext);
}

export function hasEmptyChildren(selectedElement: SlateElement) {
  return (
    selectedElement.children.length === 1 &&
    selectedElement.children[0].text === ''
  );
}

export const withLists = (editor: Editor) => {
  const { normalizeNode } = editor;

  editor.normalizeNode = (entry) => {
    if (!editor.skipListNormalizing) {
      const [node, path] = entry;
      if (SlateElement.isElement(node)) {
        if (node.type === 'li') {
          if (path.length === 1) {
            // li as the root not allowed, hence replace it with p
            Transforms.setNodes(editor, { type: 'p' }, { at: path });
            return;
          }
          if (path.length > 1) {
            const parent = SlateNode.parent(editor, path);
            if (
              SlateElement.isElement(parent) &&
              !LIST_TYPES.includes(parent.type)
            ) {
              // li not under ul or ol, hence replace it with p
              Transforms.setNodes(editor, { type: 'p' }, { at: path });
              return;
            }
          }
        } else if (LIST_TYPES.includes(node.type)) {
          for (const [child, childPath] of SlateNode.children(editor, path)) {
            if (!SlateElement.isElement(child) || child.type !== 'li') {
              Transforms.unwrapNodes(editor, { at: childPath });
              return;
            }
          }
        }
      }
    }
    // Fall back to the original `normalizeNode` to enforce other constraints.
    normalizeNode(entry);
  };

  return editor;
};
